// src/components/ErrorFallback.tsx
import React from 'react'

import type { FallbackProps } from 'react-error-boundary'
import logo from '@assets/logoCrediper_20Anni.svg'
import logo_mobile from '@assets/logoMobile.svg'
//import CloseIcon from '@ui/components/svg/ico_x'
import errorPageIcon from '@assets/ErrorPageIcon.svg'
//import AccordionHeader from '@ui/components/AccordionButton'

/**
 * ErrorFallback
 * Standard error page styled like the CompletedPage. Title and subtitle are
 * fixed (non-configurable). Shows a retry button and a small debug block with
 * the error message/stack for troubleshooting.
 *
 * Accessibility & Security notes:
 * - The error details are rendered as plain text for debugging; avoid exposing
 *   sensitive information in production (consider gating by NODE_ENV).
 */
export function ErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  const defaultTitle = 'Ops, qualcosa è andato storto, riprova più tardi'
  const defaultSubtitle = 'Se lo preferisci, puoi richiedere l’aiuto di un consulente tramite chat o chiamando il numero 0662272060'

  const rawMessage = typeof error === 'string' ? error : (error && (error as any).message) || ''
  // Strip the "Error: " prefix that appears when errors are double-wrapped (e.g. new Error(err.message))
  const raw = rawMessage.startsWith('Error: ') ? rawMessage.slice(7) : rawMessage
  const message = raw
  const stack = typeof error === 'object' && error ? (error as any).stack : undefined

  // If backend returns a message in the format "Title#Subtitle[#ButtonLabel#ButtonUrl]",
  // pick the first two segments as title/subtitle and optionally use the 3rd/4th
  // segments as the button label and URL.
  const [displayTitle, displaySubtitle, buttonLabel, buttonUrl] = React.useMemo(
    (): [string, string, string | undefined, string | undefined] => {
      if (raw && raw.includes('#')) {
        const parts = raw.split('#').map((s: string) => s.trim())
        const title = parts[0] || defaultTitle
        const subtitle = parts[1] || defaultSubtitle
        const label = parts[2] || undefined
        const url = parts[3] || undefined
        return [title, subtitle, label, url]
      }

      return [defaultTitle, defaultSubtitle, undefined, undefined]
    },
    [raw]
  )

  console.error('ErrorBoundary caught an error:', error)

  //const [accordionOpen, setAccordionOpen] = React.useState(false)

  // Minimal, self-contained fallback to avoid importing app components
  return (
    <div className='w-full min-h-screen flex flex-col bg-[linear-gradient(180deg,rgba(248,249,250,0)_17.59%,rgba(216,233,195,0.5)_135.26%)]'>
      <header className='sticky top-0 z-50 bg-white'>
        <div className='max-w-[1296px] mx-auto px-3 md:px-0 flex justify-center border-b border-b-(--border-top)'>
          <div className='flex justify-between items-center py-2 w-full'>
            <div className='flex items-center gap-3'>
              <img src={logo_mobile} alt='logo mobile' className='block md:hidden' />
              <img src={logo} alt='logo desktop' className='hidden md:block' />
            </div>

            {/* <div>
              <button
                type='button'
                onClick={() => window.location.reload()}
                className='group w-6 h-6 md:w-8 md:h-8 border-[1.6px] md:border-[3.5px] border-(--green-default) cursor-pointer flex items-center justify-center rounded-full text-base md:text-lg text-(--green-default) hover:bg-(--green-default) hover:text-white'
              >
                <CloseIcon className='transition-transform duration-300' />
              </button>
            </div> */}
          </div>
        </div>
      </header>

      <main className='flex-1'>
        <div className='w-full max-w-[1296px] mx-auto px-3 md:px-6 flex flex-col items-center gap-6 pt-10 pb-15 md:pb-30'>
          <h1 className='text-[28px] md:text-[40px] font-bold text-(--primary-blue) text-center'>
            {displayTitle}
          </h1>

          <img
            src={errorPageIcon}
            alt='errore'
            className='w-[220px] md:w-[280px]'
          />

          <p className='mb-3 md:mb-3 font-medium text-[18px] md:text-base text-center text-[#202020] max-w-[480px] md:max-w-[750px]'>
            {displaySubtitle}
          </p>

          {buttonLabel && buttonUrl && (
            <button
              type='button'
              onClick={() => {
                if (buttonUrl) {
                  window.location.href = buttonUrl
                return
              }
              }}
              className='font-bold px-6 py-3 rounded-[30px] text-white bg-(--green-default) bg-linear-to-r from-(--green-default) to-(--green-hover) shadow-[0_3px_10px_rgba(0,0,0,0.1)] cursor-pointer hover:underline'
            >
              {buttonLabel}
            </button>
          )}
        </div>

        {/* <div className='w-full max-w-[1296px] mx-auto px-3 md:px-6 flex flex-col gap-8'>
          <div className='bg-white shadow-[0px_3px_10px_0px_#0000001A] rounded-xs w-full flex flex-col mt-2.5 mb-7 md:mb-20'>
            <AccordionHeader
              label='Credito Trasparente'
              open={accordionOpen}
              onToggle={() => setAccordionOpen(!accordionOpen)}
            />

            {accordionOpen && (
              <div className='w-full bg-(--pearl-grey) py-5 px-3 md:py-8 md:px-12 rounded-b-xs flex flex-col gap-4'>
                <div className='w-full min-w-0 wrap-break-words'>{'creditoTrasparente'}</div>
              </div>
            )}
          </div>
        </div> */}
        {/* {message && (
          <div className='w-full max-w-[1296px] mx-auto px-3 md:px-6 mt-4'>
            <pre className='text-xs text-red-600 break-words'>{message}</pre>
            {stack && <pre className='text-[12px] text-[#666] break-words'>{stack}</pre>}
          </div>
        )} */}
      </main>
    </div>
  )
}
